import axios, {
  AxiosInstance,
  AxiosInterceptorManager,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { JWT_KEY } from "../../config/constant";
import { renewAccessToken } from "../auth";

export interface BaseResponse<T = any> {
  code: number;
  isSuccess: boolean;
  message: string;
  result: T;
}

interface CustomInstance extends AxiosInstance {
  interceptors: {
    request: AxiosInterceptorManager<AxiosRequestConfig>;
    response: AxiosInterceptorManager<AxiosResponse<BaseResponse>>;
  };
  getUri(config?: AxiosRequestConfig): string;
  request<T>(config: AxiosRequestConfig): Promise<T>;
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  head<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  options<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
}

const request: CustomInstance = axios.create({
  baseURL: process.env.REACT_APP_API,
  headers: {
    accept: "application/json",
  },
});

request.interceptors.request.use(
  (config) => {
    const jwt = window.localStorage.getItem(JWT_KEY) ?? "";

    if (!jwt) return config;

    if (config.headers && !config.headers.Authorization)
      config.headers.Authorization = `Bearer ${jwt}`;

    return config;
  },
  (error) => Promise.reject(error)
);

// 재발급을 모든 API가 하나로 사용할 수 있도록 Promise를 담음
let renewPromise: Promise<string | null> | null = null;

// 재발급 Promise 초기화
const clearRenewPromise = () => (renewPromise = null);

request.interceptors.response.use(
  async ({ config, data }) => {
    if (data.code === 401) {
      // renew에 대한 Promise 객체가 없을 경우
      if (!renewPromise) {
        // 신규 토큰요청을 보내고, 끝나면 clear
        renewPromise = renewAccessToken().finally(clearRenewPromise);
      }

      // Promise 끝날 때까지 대기
      const accessToken = await renewPromise;

      // 토큰이 정상적으로 넘어왔고, 이전 요청이 토큰이 있었다면
      if (accessToken && config.headers?.Authorization) {
        // 헤더 토큰 새로 넣기
        config.headers.Authorization = `Bearer ${accessToken}`;

        // 다시 요청 보내기
        return request(config);
      } else {
        throw new Error("토큰 재발급 실패");
      }
    } else if (
      data.message === "이미 탈퇴한 회원 입니다." ||
      data.message.includes("로그인 절차를 완료 하지 않은 유저 입니다.")
    ) {
      console.log(data.message);
      localStorage.clear();
      window.location.href = `${process.env.REACT_APP_URL}/login`;
      return data;
    }

    return data;
  },
  (error) => Promise.reject(error)
);

export default request;
